import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { CellSelection, TableMap } from '@tiptap/pm/tables';

/**
 * TableSelectAll
 * ---------------
 * Attaches a hover-only "select-all" button to the top-right corner of
 * every <table> inside the editor. Clicking the button selects the whole
 * table by issuing a CellSelection that spans from the first header/cell
 * to the last cell of the last row.
 *
 * The button is a single shared overlay element appended to the editor
 * container; it is repositioned over the currently-hovered table and
 * hidden otherwise. This avoids mutating the editable DOM (no node
 * decorations) and keeps the implementation independent of the table
 * node-view.
 */
const key = new PluginKey('tableSelectAll');

export const TableSelectAll = Extension.create({
  name: 'tableSelectAll',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key,
        view(view) {
          const root = view.dom as HTMLElement;
          // The overlay must escape the .ProseMirror's contentEditable so
          // clicks land on us, not on a cell. Append to the editor wrapper.
          const host = document.body;

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'we-table-select-all';
          btn.setAttribute('aria-label', 'Select entire table');
          btn.setAttribute('title', 'Select entire table');
          btn.contentEditable = 'false';
          btn.tabIndex = -1;
          btn.innerHTML =
            '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">' +
            // four-headed move arrow centered in the box
            '<path d="M8 1.5 L8 14.5 M1.5 8 L14.5 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
            '<path d="M8 1.5 L6 3.5 M8 1.5 L10 3.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" fill="none"/>' +
            '<path d="M8 14.5 L6 12.5 M8 14.5 L10 12.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" fill="none"/>' +
            '<path d="M1.5 8 L3.5 6 M1.5 8 L3.5 10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" fill="none"/>' +
            '<path d="M14.5 8 L12.5 6 M14.5 8 L12.5 10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" fill="none"/>' +
            '</svg>';
          btn.style.display = 'none';
          host.appendChild(btn);

          const rowResizeLine = document.createElement('div');
          rowResizeLine.className = 'we-table-row-resize-line';
          rowResizeLine.setAttribute('title', 'Resize row');
          rowResizeLine.contentEditable = 'false';
          rowResizeLine.style.display = 'none';
          host.appendChild(rowResizeLine);

          let currentTable: HTMLTableElement | null = null;
          let currentRow: HTMLTableRowElement | null = null;
          let resizing = false;
          let rowResizing = false;
          let startX = 0;
          let startY = 0;
          let startWidth = 0;
          let startHeight = 0;
          let nextWidth = 0;
          let nextHeight = 0;
          let startRowHeights: number[] = [];
          let hideTimer: number | null = null;
          let activePointerId: number | null = null;
          // Model position of the row being resized, captured at drag start.
          // ProseMirror's mutation observer re-renders (and detaches) the live
          // <tr> when onDragMove writes inline styles to it, which makes a
          // DOM-based row lookup fail at commit time. The doc itself does not
          // change during a pure-DOM drag, so this position stays valid.
          let rowResizePos = -1;

          const ROW_RESIZE_EDGE_THRESHOLD = 10;

          const cancelHide = () => {
            if (hideTimer !== null) {
              window.clearTimeout(hideTimer);
              hideTimer = null;
            }
          };

          const getZoom = () => {
            const canvas = root.closest('.paper-canvas') as HTMLElement | null;
            const raw = canvas?.style.getPropertyValue('--zoom') || '1';
            const parsed = Number.parseFloat(raw);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
          };

          const getTableNodeInfo = (tbl: HTMLTableElement) => {
            const tablePos = view.posAtDOM(tbl, 0);
            if (tablePos < 0) return null;
            const $start = view.state.doc.resolve(tablePos);
            let depth = $start.depth;
            while (depth > 0 && $start.node(depth).type.name !== 'table') depth--;
            if (depth <= 0) return null;
            return { pos: $start.before(depth), node: $start.node(depth) };
          };

          const mergeWidthStyle = (style: string | null | undefined, width: string) => {
            const parts = new Map<string, string>();
            String(style || '').split(';').forEach((part) => {
              const idx = part.indexOf(':');
              if (idx === -1) return;
              const key = part.slice(0, idx).trim().toLowerCase();
              const value = part.slice(idx + 1).trim();
              if (key) parts.set(key, value);
            });
            parts.set('width', width);
            parts.set('max-width', '100%');
            return Array.from(parts.entries()).map(([key, value]) => `${key}: ${value}`).join('; ');
          };

          const reposition = () => {
            if (!currentTable) return;
            const tRect = currentTable.getBoundingClientRect();
            btn.style.top = `${tRect.top - 14}px`;
            btn.style.left = `${tRect.left - 14}px`;
            if (currentRow && rowResizeLine.style.display !== 'none') {
              const rRect = currentRow.getBoundingClientRect();
              rowResizeLine.style.top = `${rRect.bottom - 8}px`;
              rowResizeLine.style.left = `${tRect.left}px`;
              rowResizeLine.style.width = `${tRect.width}px`;
            }
          };

          const showRowResizeLine = (row: HTMLTableRowElement) => {
            if (!currentTable) return;
            currentRow = row;
            rowResizeLine.style.display = 'block';
            reposition();
          };

          const hideRowResizeLine = () => {
            if (rowResizing) return;
            currentRow = null;
            rowResizeLine.style.display = 'none';
          };

          const show = (tbl: HTMLTableElement) => {
            cancelHide();
            currentTable = tbl;
            if (currentRow && !tbl.contains(currentRow) && !rowResizing) {
              hideRowResizeLine();
            }
            btn.style.display = 'flex';
            reposition();
          };

          const clampTableWithinMargins = (tbl: HTMLTableElement) => {
            const wrapper = tbl.closest('.tableWrapper') as HTMLElement | null;
            const maxWidth = Math.max(120, Math.floor(wrapper?.clientWidth || root.clientWidth || 0));
            if (!maxWidth) return;
            const tableRect = tbl.getBoundingClientRect();
            if (tableRect.width <= maxWidth + 1) return;
            const clampedWidth = `${maxWidth}px`;
            tbl.style.width = clampedWidth;
            tbl.style.minWidth = clampedWidth;
            tbl.style.maxWidth = '100%';
            tbl.setAttribute('data-table-width', clampedWidth);
          };

          const hide = () => {
            cancelHide();
            currentTable = null;
            hideRowResizeLine();
            btn.style.display = 'none';
          };

          const scheduleHide = () => {
            cancelHide();
            hideTimer = window.setTimeout(() => hide(), 180);
          };

          const onMouseOver = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            // Don't hide while the cursor is over the button itself.
            if (target === btn || btn.contains(target)) {
              cancelHide();
              return;
            }
            const tbl = target.closest('table') as HTMLTableElement | null;
            if (tbl && root.contains(tbl)) {
              if (tbl !== currentTable) show(tbl);
              else reposition();
              clampTableWithinMargins(tbl);
            } else {
              scheduleHide();
            }
          };

          const getResizeCandidate = (clientX: number, clientY: number) => {
            const hovered = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
            if (!hovered) return null;
            const table = hovered.closest('table') as HTMLTableElement | null;
            if (!table || !root.contains(table)) return null;
            const rows = Array.from(table.rows);
            if (!rows.length) return { table, row: null as HTMLTableRowElement | null, nearEdge: false };
            const hoveredRow = hovered.closest('tr') as HTMLTableRowElement | null;
            let bestRow: HTMLTableRowElement | null = null;
            let bestDistance = Number.POSITIVE_INFINITY;
            rows.forEach((row) => {
              const distance = Math.abs(row.getBoundingClientRect().bottom - clientY);
              if (distance < bestDistance) {
                bestDistance = distance;
                bestRow = row;
              }
            });
            const nearEdge = bestDistance <= ROW_RESIZE_EDGE_THRESHOLD;
            const row = hoveredRow && table.contains(hoveredRow)
              ? hoveredRow
              : (nearEdge ? bestRow : null);
            return { table, row, nearEdge };
          };

          const isColumnResizeZone = (clientX: number, clientY: number) => {
            const hovered = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
            if (!hovered) return false;
            if (hovered.closest('.column-resize-handle')) return true;
            const cell = hovered.closest('td, th') as HTMLTableCellElement | null;
            if (!cell) return false;
            const rect = cell.getBoundingClientRect();
            return Math.abs(rect.right - clientX) <= 10;
          };

          const onMouseMove = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            if (target === btn || btn.contains(target)) {
              cancelHide();
              return;
            }
            const candidate = getResizeCandidate(e.clientX, e.clientY);
            if (!candidate) {
              if (currentTable && document.body.classList.contains('resize-cursor')) {
                clampTableWithinMargins(currentTable);
              }
              if (!rowResizing) hideRowResizeLine();
              scheduleHide();
              return;
            }
            cancelHide();
            if (candidate.table !== currentTable) show(candidate.table);
            else reposition();
            clampTableWithinMargins(candidate.table);
            if (candidate.row) showRowResizeLine(candidate.row);
            else if (!rowResizing) hideRowResizeLine();
          };

          const onMouseLeave = () => scheduleHide();
          const onControlMouseEnter = () => cancelHide();
          const onControlMouseLeave = () => scheduleHide();

          const selectWholeTable = (tbl: HTMLTableElement) => {
            try {
              // Find the table node in the doc.
              const tablePos = view.posAtDOM(tbl, 0);
              if (tablePos < 0) return;
              const $start = view.state.doc.resolve(tablePos);
              // Walk up to the table node depth.
              let depth = $start.depth;
              while (depth > 0 && $start.node(depth).type.name !== 'table') depth--;
              if (depth <= 0) return;
              const tableNode = $start.node(depth);
              const tableStart = $start.before(depth) + 1; // inside table
              const map = TableMap.get(tableNode);
              if (!map.map.length) return;
              const firstCellPos = tableStart + map.map[0];
              const lastCellPos = tableStart + map.map[map.map.length - 1];
              const $a = view.state.doc.resolve(firstCellPos);
              const $b = view.state.doc.resolve(lastCellPos);
              const sel = CellSelection.create(view.state.doc, $a.pos, $b.pos);
              const tr = view.state.tr.setSelection(sel).scrollIntoView();
              view.dispatch(tr);
              view.focus();
            } catch {
              /* no-op */
            }
          };

          const onClick = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (currentTable) selectWholeTable(currentTable);
          };

          const getRowNodeInfo = (row: HTMLTableRowElement) => {
            if (!currentTable) return null;
            const tableInfo = getTableNodeInfo(currentTable);
            if (!tableInfo) return null;
            const rows = Array.from(currentTable.querySelectorAll('tr'));
            const rowIndex = rows.indexOf(row);
            if (rowIndex < 0 || rowIndex >= tableInfo.node.childCount) return null;
            let rowPos = tableInfo.pos + 1;
            for (let index = 0; index < rowIndex; index += 1) {
              rowPos += tableInfo.node.child(index).nodeSize;
            }
            return { pos: rowPos, node: tableInfo.node.child(rowIndex) };
          };

          const mergeHeightStyle = (style: string | null | undefined, height: string) => {
            const parts = new Map<string, string>();
            String(style || '').split(';').forEach((part) => {
              const idx = part.indexOf(':');
              if (idx === -1) return;
              const key = part.slice(0, idx).trim().toLowerCase();
              const value = part.slice(idx + 1).trim();
              if (key) parts.set(key, value);
            });
            parts.set('height', height);
            parts.set('min-height', height);
            return Array.from(parts.entries()).map(([key, value]) => `${key}: ${value}`).join('; ');
          };

          const commitRowHeight = () => {
            if (!Number.isFinite(nextHeight) || nextHeight <= 0) return;
            // Prefer the position captured at drag start: ProseMirror may have
            // re-rendered (detached) the live <tr> in response to our inline
            // style writes, which would make the DOM-based lookup below fail.
            let pos = rowResizePos;
            let node = pos >= 0 ? view.state.doc.nodeAt(pos) : null;
            if (!node || node.type.name !== 'tableRow') {
              const found = currentRow ? getRowNodeInfo(currentRow) : null;
              if (!found) return;
              pos = found.pos;
              node = found.node;
            }
            const height = `${Math.round(nextHeight)}px`;
            const tr = view.state.tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              style: mergeHeightStyle(node.attrs.style as string | null, height),
              height,
              'data-row-height': height
            });
            let cellPos = pos + 1;
            for (let index = 0; index < node.childCount; index += 1) {
              const cell = node.child(index);
              tr.setNodeMarkup(cellPos, undefined, {
                ...cell.attrs,
                style: mergeHeightStyle(cell.attrs.style as string | null, height),
                height,
                'data-row-height': height
              });
              cellPos += cell.nodeSize;
            }
            view.dispatch(tr);
          };

          // Live drag handlers are attached to `window` (capture) for the
          // duration of a row resize. We deliberately do NOT rely on
          // setPointerCapture(root): the cursor sits on top of the fixed
          // overlay line (which is appended to <body>, not a descendant of
          // root), so capturing to root silently failed in some browsers and
          // the drag never tracked. Window-level listeners always receive the
          // move/up regardless of what element is under the pointer.
          const onDragMove = (e: PointerEvent) => {
            if (!rowResizing || !currentRow) return;
            if (activePointerId !== null && e.pointerId !== activePointerId) return;
            e.preventDefault();
            const zoom = getZoom();
            nextHeight = Math.max(22, Math.min(1000, startHeight + (e.clientY - startY) / zoom));
            const height = `${Math.round(nextHeight)}px`;
            currentRow.style.height = height;
            currentRow.style.minHeight = height;
            currentRow.style.maxHeight = height;
            currentRow.dataset.rowHeight = height;
            Array.from(currentRow.cells).forEach((cell) => {
              cell.style.height = height;
              cell.style.minHeight = height;
              cell.style.maxHeight = height;
              cell.dataset.rowHeight = height;
            });
            reposition();
          };

          const endRowResize = () => {
            if (!rowResizing) return;
            rowResizing = false;
            activePointerId = null;
            document.body.classList.remove('we-table-row-resizing');
            window.removeEventListener('pointermove', onDragMove, true);
            window.removeEventListener('pointerup', onDragEnd, true);
            window.removeEventListener('pointercancel', onDragEnd, true);
            commitRowHeight();
            rowResizePos = -1;
            reposition();
          };

          function onDragEnd(e: PointerEvent) {
            if (!rowResizing) return;
            if (activePointerId !== null && e.pointerId !== activePointerId) return;
            e.preventDefault();
            e.stopPropagation();
            endRowResize();
          }

          const startRowResize = (
            clientY: number,
            row: HTMLTableRowElement,
            table: HTMLTableElement,
            pointerId: number | null
          ) => {
            if (rowResizing) return;
            if (!row || !table) return;
            currentRow = row;
            currentTable = table;
            rowResizing = true;
            activePointerId = pointerId;
            // Capture the row's model position now, while the <tr> is still the
            // live element, so commitRowHeight can find it even after PM
            // re-renders the row during the drag.
            const startInfo = getRowNodeInfo(row);
            rowResizePos = startInfo ? startInfo.pos : -1;
            cancelHide();
            startY = clientY;
            startHeight = row.offsetHeight || row.getBoundingClientRect().height / getZoom();
            nextHeight = startHeight;
            document.body.classList.add('we-table-row-resizing');
            rowResizeLine.style.display = 'block';
            reposition();
            window.addEventListener('pointermove', onDragMove, true);
            window.addEventListener('pointerup', onDragEnd, true);
            window.addEventListener('pointercancel', onDragEnd, true);
          };

          const onRootPointerDown = (e: PointerEvent) => {
            if (e.button !== 0) return;
            if (rowResizing) return;
            const target = e.target as HTMLElement | null;
            if (!target) return;
            if (target === btn || btn.contains(target)) return;
            if (isColumnResizeZone(e.clientX, e.clientY)) return;
            const candidate = getResizeCandidate(e.clientX, e.clientY);
            if (!candidate || !candidate.row || !candidate.nearEdge) return;
            e.preventDefault();
            e.stopPropagation();
            startRowResize(e.clientY, candidate.row, candidate.table, e.pointerId);
          };

          const onRowLinePointerDown = (e: PointerEvent) => {
            if (rowResizing) return;
            const table = currentTable;
            const row = currentRow;
            if (!table || !row) return;
            e.preventDefault();
            e.stopPropagation();
            startRowResize(e.clientY, row, table, e.pointerId);
          };

          // mousedown happens before the editor steals focus; preventing
          // default keeps the editor selection intact for the click.
          const onMouseDown = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
          };

          root.addEventListener('mouseover', onMouseOver);
          root.addEventListener('mousemove', onMouseMove);
          root.addEventListener('mouseleave', onMouseLeave);
          root.addEventListener('pointerdown', onRootPointerDown, true);
          btn.addEventListener('mousedown', onMouseDown);
          btn.addEventListener('click', onClick);
          btn.addEventListener('mouseenter', onControlMouseEnter);
          btn.addEventListener('mouseleave', onControlMouseLeave);
          rowResizeLine.addEventListener('pointerdown', onRowLinePointerDown);
          rowResizeLine.addEventListener('mouseenter', onControlMouseEnter);
          rowResizeLine.addEventListener('mouseleave', onControlMouseLeave);
          window.addEventListener('scroll', reposition, true);
          window.addEventListener('resize', reposition);

          return {
            update() {
              if (currentTable && document.contains(currentTable)) reposition();
              else hide();
            },
            destroy() {
              root.removeEventListener('mouseover', onMouseOver);
              root.removeEventListener('mousemove', onMouseMove);
              root.removeEventListener('mouseleave', onMouseLeave);
              root.removeEventListener('pointerdown', onRootPointerDown, true);
              btn.removeEventListener('mousedown', onMouseDown);
              btn.removeEventListener('click', onClick);
              btn.removeEventListener('mouseenter', onControlMouseEnter);
              btn.removeEventListener('mouseleave', onControlMouseLeave);
              rowResizeLine.removeEventListener('pointerdown', onRowLinePointerDown);
              rowResizeLine.removeEventListener('mouseenter', onControlMouseEnter);
              rowResizeLine.removeEventListener('mouseleave', onControlMouseLeave);
              window.removeEventListener('pointermove', onDragMove, true);
              window.removeEventListener('pointerup', onDragEnd, true);
              window.removeEventListener('pointercancel', onDragEnd, true);
              window.removeEventListener('scroll', reposition, true);
              window.removeEventListener('resize', reposition);
              cancelHide();
              btn.remove();
              rowResizeLine.remove();
            }
          };
        }
      })
    ];
  }
});
